// src/services/vendas/PDVService.js
import { Nota } from '../../models/vendas/Nota';
import { NotaItem } from '../../models/vendas/NotaItem';
import { NotaPag } from '../../models/vendas/NotaPag';
import { MeioPagamento } from '../../models/financeiro/FormaPag';
import { fetchBlobData, fetchData, postData, putData } from '../Api';
import { pagamentoExigeTef, terminalDisponivelPagamento } from '../TerminalPagamentoService';
import {
    fromMoneyCents,
    formatNumeric,
    normalizeMoney,
    parseCurrency,
    parseNumeric,
    roundDecimal,
    sumMoney,
    toMoneyCents
} from '../../utils/numberUtils';

export const IntegradorTEFEnum = Object.freeze({
    Nenhum: 0,
    MSiTef: 1,
    SuperTef: 2,
    Cielo: 3,
    Banrisul: 4,
    Manual: 5
});

export const integradorTefDisponivelPDV = terminalDisponivelPagamento;

export const calcularTotalItensPDV = (itens = []) => {
    return sumMoney(itens.map((item) => item?.vProd));
};

export const obterItemSemValor = (itens = []) => {
    return itens.find((item) => !item.vProd || parseCurrency(item.vProd) === 0) || null;
};

export const criarNotaFinalizacaoPDV = ({ total, itensPDV }) => {
    const notaPayload = new Nota();
    const totalNormalizado = normalizeMoney(total);

    return {
        ...notaPayload,
        caixaId: 1,
        vProd: totalNormalizado,
        vNota: totalNormalizado,
        clienteId: 1,
        tipoMov: 2,
        notaItens: itensPDV
    };
};

export const inicializarClienteFinalizacaoPDV = ({ nota = {}, clientes = [] }) => {
    const clienteId = nota.clienteId || 1;
    const cliente = clientes.find(item => Number(item.id) === Number(clienteId))
        || (Number(nota.cliente?.id) === Number(clienteId) ? nota.cliente : null);
    const manteveCliente = Number(nota.clienteId) === Number(clienteId);
    const campos = {
        clienteId,
        cliente,
        clienteNome: (manteveCliente && nota.clienteNome) || cliente?.pessoa?.nome || '',
        cpf: (manteveCliente && nota.cpf) || cliente?.pessoa?.fisica?.cpf || '',
        cnpj: (manteveCliente && nota.cnpj) || cliente?.pessoa?.juridica?.cnpj || ''
    };

    return Object.entries(campos).every(([campo, valor]) => nota[campo] === valor)
        ? nota : { ...nota, ...campos };
};

export const atualizarNotaFinalizacaoPDV = ({ nota, total, notaPags = [], itensPDV }) => {
    const totalNormalizado = normalizeMoney(total);
    return {
        ...nota,
        notaPags,
        notaItens: itensPDV,
        vProd: totalNormalizado,
        vNota: fromMoneyCents(
            toMoneyCents(totalNormalizado)
            - toMoneyCents(nota.vDesc)
            + toMoneyCents(nota.vFrete)
            + toMoneyCents(nota.vOutro)
        )
    };
};

export const criarItemPDVPorProduto = ({ produto, numItem, quantidade = 1 }) => {
    const newItemPDV = new NotaItem();

    newItemPDV.produtoId = produto.id;
    newItemPDV.item = numItem;
    newItemPDV.cfop = produto.cfop;
    newItemPDV.unCom = produto.unTrib;
    newItemPDV.qtdCom = quantidade;
    newItemPDV.vUnCom = formatNumeric(produto.preco);
    newItemPDV.vProd = calcularTotalItemPDV({ quantidade, valorUnitario: produto.preco });
    newItemPDV.xProd = produto.descricao;
    newItemPDV.produto = produto;

    return newItemPDV;
};

export const calcularTotalItemPDV = ({ quantidade, valorUnitario }) => {
    return normalizeMoney(parseCurrency(quantidade) * parseCurrency(valorUnitario));
};

export const calcularValorNotaPDV = (notaAtual, novoFrete = 0) => {
    return fromMoneyCents(
        toMoneyCents(notaAtual?.vProd)
        - toMoneyCents(notaAtual?.vDesc)
        + toMoneyCents(novoFrete)
        + toMoneyCents(notaAtual?.vOutro)
    );
};

const validarQuantidadeItemFiscal = (item, descricaoFallback) => {
    const quantidade = parseNumeric(item?.qtde ?? item?.qtdCom);
    if (!Number.isFinite(quantidade) || quantidade <= 0) {
        throw new Error(`O item "${item?.produto?.descricao || item?.xProd || descricaoFallback}" possui quantidade inválida.`);
    }
    return quantidade;
};

/**
 * Converte um pedido em nota mantendo as invariantes fiscais: a soma dos itens
 * forma vProd, cashback utilizado compõe vDesc e vNota é sempre derivado desses
 * valores em centavos. Divergências reais são bloqueadas antes de abrir o modal.
 */
export const montarNotaPedidoPDV = ({ pedido, clienteId }) => {
    const itensPedido = Array.isArray(pedido?.pedidoItens) ? pedido.pedidoItens : [];
    if (itensPedido.length === 0) {
        throw new Error('O pedido não possui itens para emissão da nota.');
    }

    const notaItens = itensPedido.map((item, index) => {
        const quantidade = validarQuantidadeItemFiscal(item, `#${index + 1}`);
        const vProd = normalizeMoney(item.valorTotal);
        const notaItem = new NotaItem();
        notaItem.produtoId = item.produtoId;
        notaItem.xProd = item.produto?.descricao || '';
        notaItem.cfop = item.produto?.cfop || '';
        notaItem.unCom = item.produto?.unMedida?.sigla || '';
        notaItem.item = index + 1;
        notaItem.qtdCom = quantidade;
        notaItem.vUnCom = roundDecimal(vProd / quantidade, 10);
        notaItem.vProd = vProd;
        return notaItem;
    });

    const vProd = sumMoney(notaItens.map((item) => item.vProd));
    if (toMoneyCents(vProd) !== toMoneyCents(pedido?.valorItens)) {
        throw new Error('O total dos itens do pedido diverge da soma dos itens. Atualize o pedido antes de emitir a nota.');
    }

    const vDesc = fromMoneyCents(
        toMoneyCents(pedido?.valorDesconto) + toMoneyCents(pedido?.valorCashBack)
    );
    const vOutro = normalizeMoney(pedido?.valorAcrescimo);
    const vFrete = normalizeMoney(pedido?.taxaEntrega);
    const vNota = fromMoneyCents(
        toMoneyCents(vProd) - toMoneyCents(vDesc) + toMoneyCents(vOutro) + toMoneyCents(vFrete)
    );

    if (toMoneyCents(vNota) !== toMoneyCents(pedido?.valorTotal)) {
        throw new Error('O total do pedido diverge dos itens, desconto, cashback, acréscimo e entrega. Atualize o pedido antes de emitir a nota.');
    }

    const notaPayload = new Nota();
    notaPayload.caixaId = 1;
    notaPayload.clienteId = clienteId || pedido?.clienteAppId || 1;
    notaPayload.pedidoId = pedido?.id;
    notaPayload.tipoMov = 2;
    notaPayload.vProd = vProd;
    notaPayload.vDesc = vDesc;
    notaPayload.vOutro = vOutro;
    notaPayload.vFrete = vFrete;
    notaPayload.vNota = vNota;
    notaPayload.notaItens = notaItens;

    if (Number(pedido?.formaPagId) > 0) {
        const pagamento = new NotaPag();
        pagamento.formaPagId = Number(pedido.formaPagId);
        pagamento.formaPag = pedido.formaPag || pagamento.formaPag;
        pagamento.vPag = vNota;
        pagamento.vRec = vNota;
        pagamento.vTroco = 0;
        pagamento.pago = false;
        notaPayload.notaPags = [pagamento];
    }

    return notaPayload;
};

const listarItensComanda = (comandas = []) => comandas.flatMap(
    (comanda) => Array.isArray(comanda?.comandaItens) ? comanda.comandaItens : []
);

const obterSaldoItemCentavos = (item) => Math.max(
    0,
    toMoneyCents(item?.valorTotal) - toMoneyCents(item?.valorPago)
);

/**
 * Rateia um total em centavos apenas entre itens com saldo. As sobras são
 * distribuídas pelas maiores frações, sem ultrapassar o saldo de nenhum item.
 */
export const ratearTotalMesaPDV = ({ comandas = [], total }) => {
    const itens = listarItensComanda(comandas);
    const elegiveis = itens
        .map((item, indice) => ({ item, indice, saldo: obterSaldoItemCentavos(item) }))
        .filter(({ saldo }) => saldo > 0);
    const saldoTotal = elegiveis.reduce((soma, atual) => soma + atual.saldo, 0);
    const totalCentavos = toMoneyCents(total);

    if (totalCentavos < 0 || totalCentavos > saldoTotal) {
        throw new Error(`O valor a pagar não pode superar o saldo de ${fromMoneyCents(saldoTotal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`);
    }

    const rateio = Object.fromEntries(itens.map((item) => [item.id, 0]));
    if (totalCentavos === 0 || saldoTotal === 0) {
        return rateio;
    }

    const parcelas = elegiveis.map(({ item, indice, saldo }) => {
        const numerador = totalCentavos * saldo;
        const base = Math.floor(numerador / saldoTotal);
        return { item, indice, saldo, base, resto: numerador % saldoTotal };
    });

    let restantes = totalCentavos - parcelas.reduce((soma, parcela) => soma + parcela.base, 0);
    const ordenadas = [...parcelas].sort((a, b) => b.resto - a.resto || a.indice - b.indice);
    for (const parcela of ordenadas) {
        if (restantes <= 0) break;
        if (parcela.base < parcela.saldo) {
            parcela.base += 1;
            restantes -= 1;
        }
    }

    parcelas.forEach(({ item, base }) => {
        rateio[item.id] = fromMoneyCents(base);
    });
    return rateio;
};

const validarValoresMesaPDV = ({ comandas = [], valores = {} }) => {
    const itens = listarItensComanda(comandas);
    const pagamentos = itens.map((item) => {
        const valor = toMoneyCents(valores[item.id]);
        const saldo = obterSaldoItemCentavos(item);
        if (valor < 0 || valor > saldo) {
            const descricao = item?.produto?.descricao || item?.xProd || `#${item.id}`;
            throw new Error(`O valor informado para "${descricao}" supera o saldo do item.`);
        }
        return { item, valor };
    });

    return {
        pagamentos: pagamentos.filter(({ valor }) => valor > 0),
        total: pagamentos.reduce((soma, pagamento) => soma + pagamento.valor, 0)
    };
};

/**
 * Monta a nota de uma mesa/comanda somente com saldos em aberto. O pagamento
 * não é criado aqui: o ModalFinalizarVenda é a fonte única da forma recebida,
 * do valor efetivamente pago e do eventual troco em dinheiro.
 */
export const montarNotaMesaPDV = ({ clienteId, comandas = [], valores = {} }) => {
    const { pagamentos, total } = validarValoresMesaPDV({ comandas, valores });
    if (total <= 0 || pagamentos.length === 0) {
        throw new Error('Não há valor em aberto para finalizar.');
    }

    const notaPayload = new Nota();
    notaPayload.caixaId = 1;
    notaPayload.clienteId = clienteId || 1;
    notaPayload.tipoMov = 2;
    notaPayload.vProd = fromMoneyCents(total);
    notaPayload.vNota = fromMoneyCents(total);
    notaPayload.notaItens = pagamentos.map(({ item, valor }, index) => {
        const quantidade = validarQuantidadeItemFiscal(item, `#${item.id}`);
        const vProd = fromMoneyCents(valor);
        const notaItem = new NotaItem();
        notaItem.comandaItemId = item.id;
        notaItem.produtoId = item.produtoId;
        notaItem.xProd = item.produto?.descricao || item.xProd;
        notaItem.cfop = item.produto?.cfop || '';
        notaItem.unCom = item.produto?.unMedida?.sigla || '';
        notaItem.item = index + 1;
        notaItem.qtdCom = quantidade;
        notaItem.vUnCom = roundDecimal(vProd / quantidade, 10);
        notaItem.vProd = vProd;
        return notaItem;
    });
    notaPayload.notaPags = [];

    return notaPayload;
};

/**
 * Prepara os pagamentos sempre a partir da nota recebida na abertura atual.
 * A função não lê estado React, portanto uma venda reaberta não herda valores
 * da anterior. Troco é recalculado em centavos e existe somente em dinheiro.
 */
export const inicializarPagamentosFinalizacaoPDV = ({ nota = {}, formasPag = [] }) => {
    if (!Array.isArray(formasPag) || formasPag.length === 0) {
        return [];
    }

    const formasAtivas = formasPag.filter(forma => !forma.inativo);
    const formaDinheiro = formasAtivas.find(
        (formaPag) => Number(formaPag.meioPag) === MeioPagamento.Dinheiro
    ) || formasAtivas[0];
    const pagamentosOrigem = Array.isArray(nota?.notaPags) ? nota.notaPags : [];
    const pagamentosBase = pagamentosOrigem.length > 0
        ? pagamentosOrigem
        : [{ ...new NotaPag(), formaPagId: formaDinheiro?.id, formaPag: formaDinheiro }];

    return pagamentosBase.map((pagamento, index) => {
        const formaPag = formasPag.find(
            (forma) => Number(forma.id) === Number(pagamento.formaPagId)
        ) || pagamento.formaPag;
        const possuiPagamentoOrigem = pagamentosOrigem.length > 0;
        const vPag = normalizeMoney(
            possuiPagamentoOrigem ? pagamento.vPag ?? nota.vNota : pagamento.vPag
        );
        const vRec = normalizeMoney(
            possuiPagamentoOrigem ? pagamento.vRec ?? pagamento.vPag ?? nota.vNota : pagamento.vRec
        );
        const ehDinheiro = Number(formaPag?.meioPag) === MeioPagamento.Dinheiro;

        return {
            ...new NotaPag(),
            ...pagamento,
            item: pagamento.item || index + 1,
            formaPagId: formaPag?.id || pagamento.formaPagId,
            formaPag: formaPag || pagamento.formaPag,
            vPag,
            vRec,
            vTroco: ehDinheiro
                ? fromMoneyCents(Math.max(0, toMoneyCents(vRec) - toMoneyCents(vPag)))
                : 0,
            pago: pagamento.pago === true || (ehDinheiro && toMoneyCents(vRec) > 0)
        };
    });
};

export const isFormaDinheiroPDV = (formaPag) => {
    const descricao = (formaPag?.descricao || '').trim().toLowerCase();
    return Number(formaPag?.meioPag) === MeioPagamento.Dinheiro || descricao === 'dinheiro';
};

export const deveMostrarTerminalPDV = ({ formaPag, configTEFList = [] }) => {
    const terminaisDisponiveis = configTEFList.filter(integradorTefDisponivelPDV);
    return formaPag?.usaTef === true && terminaisDisponiveis.length > 1;
};

export const getDanfeNFCePDFByNotaIdService = async ({ notaId, paperWidthMm = 75 }) => {
    return fetchBlobData('/Relatorio/DanfeNfcePDFByNotaId', { notaId, paperWidthMm });
};

export const getDanfsePDFByNotaIdService = async ({ notaId, paperWidthMm = 80 }) => {
    return fetchBlobData('/NFSe/DanfsePdf', { notaId, paperWidthMm });
};

export const getDanfseA4PDFByNotaIdService = async ({ notaId }) => {
    return fetchBlobData('/NFSe/DanfsePdfA4', { notaId });
};

export const getReciboPDFByNotaIdService = async ({ notaId, paperWidthMm = 75 }) => {
    return fetchBlobData('/Relatorio/ReciboPdfByNotaId', { notaId, paperWidthMm });
};

export const calcularTotalPagoPDV = (notaPags = []) => {
    if (!Array.isArray(notaPags)) {
        return 0;
    }

    return sumMoney(notaPags.map((pag) => pag?.vPag));
};

export const montarPayloadNovaNotaPDV = ({ nota, notaPags }) => {
    const pagamentos = (notaPags || []).map((pagamento) => ({
        ...pagamento,
        vPag: normalizeMoney(pagamento.vPag),
        vRec: normalizeMoney(pagamento.vRec),
        vTroco: normalizeMoney(pagamento.vTroco),
        qtdeParcela: pagamento.qtdeParcela || null,
        dataVencimento: pagamento.dataVencimento || null
    }));
    const itens = (nota?.notaItens || []).map((item) => ({
        ...item,
        vProd: normalizeMoney(item.vProd),
        vUnCom: roundDecimal(item.vUnCom, 10)
    }));
    const payload = {
        ...nota,
        vProd: normalizeMoney(nota?.vProd),
        vDesc: normalizeMoney(nota?.vDesc),
        vOutro: normalizeMoney(nota?.vOutro),
        vFrete: normalizeMoney(nota?.vFrete),
        vNota: normalizeMoney(nota?.vNota),
        notaItens: itens,
        situacaoNF: 10,
        notaPags: pagamentos
    };

    delete payload.natureza;

    return payload;
};

const CAMPOS_FINALIZACAO_NOTA = [
    'id',
    'clienteId',
    'clienteNome',
    'naturezaId',
    'cpf',
    'cnpj',
    'vProd',
    'vDesc',
    'vOutro',
    'vFrete',
    'vNota',
    'observacao',
    'situacaoNF'
];

export const montarPayloadFinalizacaoPDV = (nota = {}, pagamentos) => {
    const payload = CAMPOS_FINALIZACAO_NOTA.reduce((payload, campo) => {
        const camposMonetarios = ['vProd', 'vDesc', 'vOutro', 'vFrete', 'vNota'];
        payload[campo] = camposMonetarios.includes(campo)
            ? normalizeMoney(nota?.[campo])
            : nota?.[campo] ?? null;
        return payload;
    }, {});
    if (pagamentos !== undefined) {
        payload.notaPags = pagamentos.map(pagamento => ({
            id: Number(pagamento.id) || 0,
            item: pagamento.item,
            formaPagId: Number(pagamento.formaPagId),
            vPag: normalizeMoney(pagamento.vPag),
            vRec: normalizeMoney(pagamento.vRec),
            vTroco: normalizeMoney(pagamento.vTroco),
            qtdeParcela: pagamento.qtdeParcela || null,
            dataVencimento: pagamento.dataVencimento || null,
            configTEFid: pagamento.configTEFid ?? null,
            tefNaoIntegrado: pagamento.tefNaoIntegrado === true
        }));
    }
    return payload;
};

export const houveAlteracaoFinalizacaoPDV = (notaAtual, notaPersistida) => {
    if (!notaPersistida) {
        return false;
    }

    return JSON.stringify(montarPayloadFinalizacaoPDV(notaAtual)) !==
        JSON.stringify(montarPayloadFinalizacaoPDV(notaPersistida));
};

export const atualizarFinalizacaoNotaPDV = async (nota, pagamentos) => {
    const payload = montarPayloadFinalizacaoPDV(nota, pagamentos);
    console.info('[PDV] Atualizando finalização da nota.', {
        notaId: payload.id,
        etapa: 'atualizar-finalizacao'
    });
    return putData('/Nota/Finalizacao', payload);
};

export const deveEmitirNotaFiscalPDV = (notaPags = [], terminal) => {
    return notaPags.some((notaPag) => pagamentoExigeTef(notaPag, terminal));
};

export const classificarItensFiscaisPDV = (notaItens = []) => {
    const itens = Array.isArray(notaItens) ? notaItens : [];
    return {
        possuiProdutos: itens.some(item => item?.produto?.ehServico !== true),
        possuiServicos: itens.some(item => item?.produto?.ehServico === true)
    };
};

export const validarEnderecoClienteFretePDV = ({ notaAtual, clientes = [] }) => {
    const clienteSelecionado = clientes.find(
        f => f.id.toString() === (notaAtual?.clienteId || '').toString()
    ) || notaAtual?.cliente;

    if (!clienteSelecionado) {
        return {
            valido: false,
            mensagem: 'Não foi possível validar o endereço do cliente selecionado para emitir a nota com frete.'
        };
    }

    const endereco = clienteSelecionado?.pessoa?.endereco || clienteSelecionado?.endereco || {};
    const camposObrigatorios = [
        { chave: 'logradouro', label: 'Logradouro' },
        { chave: 'numero', label: 'Número' },
        { chave: 'bairro', label: 'Bairro' }
    ];

    const camposFaltantes = camposObrigatorios
        .filter(({ chave }) => !(endereco?.[chave] || '').toString().trim())
        .map(({ label }) => label);

    if (camposFaltantes.length > 0) {
        return {
            valido: false,
            mensagem: `Para emitir nota com frete, informe no endereço do cliente: ${camposFaltantes.join(', ')}.`
        };
    }

    return { valido: true, mensagem: '' };
};

export const emitirNFePDV = async (notaId) => {
    return postData(`/DFe/EmitirNFe?notaId=${notaId}`);
};

export const emitirNFCePDV = async (notaId) => {
    return postData(`/DFe/EmitirNFCe?notaId=${notaId}`);
};

export const emitirVendaFiscalPDV = async (request) => {
    return postData('/NFSe/EmitirVenda', request);
};

export const solicitarImpressaoNFCeMonitorDesktopPDV = async (notaId) => {
    return postData(`/DFe/SolicitarImpressaoNFCe?notaId=${notaId}`);
};

export const solicitarImpressaoNFSeMonitorPDV = async (notaId) => {
    return postData(`/NFSe/SolicitarImpressao?notaId=${notaId}`);
};

export const solicitarImpressaoReciboMonitorDesktopPDV = async (notaId) => {
    return postData(`/DFe/SolicitarImpressaoRecibo?notaId=${notaId}`);
};

export const emitirBoletoPDV = async (notaPagId) => {
    return postData(`/Boleto/EmitirBoleto?notaPagId=${notaPagId}`, { responseType: 'blob' });
};

export const solicitarPagamentoPOSPDV = async ({ notaPagId, configTEFId }) => {
    return postData('/POS/SolicitaPagamento', {
        notaPagId,
        configTEFId
    });
};

export const solicitarPagamentoMonitorTEFPDV = async ({ notaPagId, configTEFId }) => {
    return postData('/MonitorTEF/SolicitarPagamento', {
        notaPagId,
        configTEFId
    });
};

export const confirmarTefManualPDV = async ({
    notaPagId,
    configTEFId,
    autorizacao,
    bandeira,
    tefNaoIntegrado
}) => postData('/NotaPag/ConfirmarTefManual', {
    notaPagId,
    configTEFId,
    autorizacao,
    bandeira,
    tefNaoIntegrado
});

export const buscarStatusPagamentoMonitorTEFPDV = async (notaPagId) => {
    return fetchData('/MonitorTEF/BuscarStatusPagamento', { notaPagId });
};

export const obterStatusTEFMonitorPDV = (retorno) => Number(
    retorno?.statusTEF ??
    retorno?.StatusTEF ??
    0
);

export const deveSolicitarPagamentoMonitorTEF = (retorno) => {
    const statusTEF = obterStatusTEFMonitorPDV(retorno);
    const pago = retorno?.pago === true || retorno?.Pago === true;
    return !pago && statusTEF !== 1 && statusTEF !== 2;
};

export const aguardarPagamentoMonitorTEF = async ({
    notaPagId,
    timeoutMs = 120000,
    intervaloMs = 1000,
    deveCancelar = () => false,
    buscarStatus = buscarStatusPagamentoMonitorTEFPDV
}) => {
    const inicio = Date.now();
    let ultimoRetorno = null;
    let ultimoErro = null;

    while (Date.now() - inicio < timeoutMs) {
        if (deveCancelar()) {
            return {
                status: 'pendente',
                cancelado: true,
                retorno: ultimoRetorno
            };
        }

        try {
            ultimoRetorno = await buscarStatus(notaPagId);
            ultimoErro = null;

            const statusTEF = obterStatusTEFMonitorPDV(ultimoRetorno);

            if (statusTEF === 2 || ultimoRetorno?.pago === true || ultimoRetorno?.Pago === true) {
                return {
                    status: 'aprovado',
                    retorno: ultimoRetorno
                };
            }

            if (statusTEF === 3) {
                return {
                    status: 'rejeitado',
                    retorno: ultimoRetorno
                };
            }
        } catch (error) {
            ultimoErro = error;
        }

        await new Promise(resolve => setTimeout(resolve, intervaloMs));
    }

    return {
        status: 'timeout',
        retorno: ultimoRetorno,
        erro: ultimoErro
    };
};

export const listarPagamentoEspecificoPOSPDV = async (notaPagId) => {
    return fetchData('/POS/ListaPagamentoEspecifico', { notaPagId });
};

export const aguardarPagamentoPOSPDV = async ({
    notaPagId, timeoutMs = 120000, intervaloMs = 1000,
    deveCancelar = () => false, buscarStatus = listarPagamentoEspecificoPOSPDV
}) => {
    return aguardarPagamentoMonitorTEF({
        notaPagId, timeoutMs, intervaloMs, deveCancelar,
        buscarStatus: async (id) => {
            const retorno = await buscarStatus(id);
            return {
                ...retorno,
                statusTEF: Number(retorno?.status) === 4 ? 2 : Number(retorno?.status) === 5 ? 3 : 1
            };
        }
    });
};
